import React, { useEffect, useState } from 'react';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { useSelectIsAppModeNewportSingleWidget } from 'app/store/general/selectors';
import { useDispatchUpdateAppSettings } from 'app/store/appSettings/dispatcher';
import AppSettingsEditor from 'appToolbar/AppSettingsEditor';
import {
    CheckboxInput,
    NumericValueEditor,
    TextInput,
    ToggleInput,
} from '@instinet-analytics/semantic-ui-components';
import { useDebouncedFunction } from '@instinet-analytics/core-lib';
import {
    ExpectedVsActualConfig,
    FillsChart,
    FillsChartType,
    FillsGraphConfiguration,
    toggleConfigProperty,
} from 'widgets/fillsGraph/store/configuration/FillsGraphConfiguration';
import './FillsGraphSettings.scss';
import { PLBenchmarkDropdown } from 'widgets/fillsGraph/pl/PLBenchmarkDropdown';
import FillsGraphExpectedVolumeSettings from 'widgets/fillsGraph/settings/FillsGraphSettingsExpectedVolume';
import SymbolInput from 'symbols/components/SymbolInput';

interface FillsGraphSettingsProps {
    fillsGraphConfiguration: FillsGraphConfiguration;
    onFillsGraphConfigurationChange: (
        fillsGraphConfiguration: FillsGraphConfiguration
    ) => void;
}

export default function FillsGraphSettings(props: FillsGraphSettingsProps) {
    const { fillsGraphConfiguration, onFillsGraphConfigurationChange } = props;
    const {
        visibleCharts,
        general: generalConfig,
        [FillsChartType.OrderFills]: orderFillsConfig,
        [FillsChartType.PercentageOfMarketVolume]:
            percentageOfMarketVolumeConfig,
        [FillsChartType.ExpectedVsActual]: expectedVsActualConfig,
    } = fillsGraphConfiguration;

    const [barSizeInputText, setBarSizeInputText] = useState(
        generalConfig.barSizeInMinutes.toString()
    );
    useEffect(() => {
        setBarSizeInputText(generalConfig.barSizeInMinutes.toString());
    }, [generalConfig.barSizeInMinutes]);

    const [fallbackIndexInputText, setFallbackIndexInputText] = useState(
        generalConfig.fallbackIndex
    );
    const [
        avgExecAndVwapDecimalsInputText,
        setAvgExecAndVwapDecimalsInputText,
    ] = useState(orderFillsConfig.avgExecAndVwapDecimals);

    useEffect(() => {
        setFallbackIndexInputText(generalConfig.fallbackIndex);
    }, [generalConfig.fallbackIndex]);

    const isAppModeNewportSingleWidget =
        useSelectIsAppModeNewportSingleWidget();
    const appSettings = useSelectAppSettings();
    const dispatchUpdateAppSettings = useDispatchUpdateAppSettings();

    const [debouncedConfigurationChange] = useDebouncedFunction(
        onFillsGraphConfigurationChange,
        2000,
        {
            callOnUnmount: true,
        }
    );

    const setBarSizeAndUpdateConfig = (barSize: string) => {
        // TODO: validation?
        setBarSizeInputText(barSize);

        const barSizeInMinutes = parseInt(barSize);
        if (isNaN(barSizeInMinutes) || barSizeInMinutes < 1) {
            return;
        }

        (
            debouncedConfigurationChange as (
                config: FillsGraphConfiguration
            ) => void
        )({
            ...fillsGraphConfiguration,
            general: {
                ...generalConfig,
                barSizeInMinutes,
            },
        });
    };

    const setFallbackIndexAndUpdateConfig = (enteredText: string) => {
        const fallbackIndex = enteredText.toUpperCase();
        setFallbackIndexInputText(fallbackIndex);
        (
            debouncedConfigurationChange as (
                config: FillsGraphConfiguration
            ) => void
        )({
            ...fillsGraphConfiguration,
            general: {
                ...generalConfig,
                fallbackIndex,
            },
        });
    };

    const toggleChartVisible = (chartType: FillsChartType) => () => {
        const index = visibleCharts.findIndex((x) => x.type === chartType);

        const updatedVisibleCharts: FillsChart[] = [...visibleCharts];
        if (index > -1) {
            updatedVisibleCharts.splice(index, 1);
        } else {
            updatedVisibleCharts.push({ type: chartType });
        }

        if (updatedVisibleCharts.length === 0) {
            if (chartType === FillsChartType.OrderFills) {
                updatedVisibleCharts.push({
                    type: FillsChartType.ExpectedVsActual,
                });
            } else {
                updatedVisibleCharts.push({ type: FillsChartType.OrderFills });
            }
        }

        onFillsGraphConfigurationChange({
            ...fillsGraphConfiguration,
            visibleCharts: updatedVisibleCharts,
        });
    };

    const isVisible = (chartType: FillsChartType) =>
        visibleCharts.some((x) => x.type === chartType);

    const toggleGeneralConfig =
        (property: keyof FillsGraphConfiguration['general']) => () => {
            onFillsGraphConfigurationChange(
                toggleConfigProperty(
                    fillsGraphConfiguration,
                    'general',
                    property
                )
            );
        };

    const toggleCompanionChartConfig =
        <T extends FillsChartType>(chart: T) =>
        (property: keyof FillsGraphConfiguration[T]) =>
        () => {
            onFillsGraphConfigurationChange(
                toggleConfigProperty(fillsGraphConfiguration, chart, property)
            );
        };

    const toggleOrderFillsConfig = toggleCompanionChartConfig(
        FillsChartType.OrderFills
    );

    const togglePercentageOfMarketVolumeConfig = toggleCompanionChartConfig(
        FillsChartType.PercentageOfMarketVolume
    );

    const onAvgExecAndVwapDecimalsChanged = (nextValue: number) => {
        setAvgExecAndVwapDecimalsInputText(nextValue);
        (
            debouncedConfigurationChange as (
                config: FillsGraphConfiguration
            ) => void
        )({
            ...fillsGraphConfiguration,
            [FillsChartType.OrderFills]: {
                ...fillsGraphConfiguration[FillsChartType.OrderFills],
                avgExecAndVwapDecimals: nextValue,
            },
        });
    };

    return (
        <div className="FillsGraphSettings">
            <div className="group group-general">
                <div className="group-header">
                    <div className="title">General</div>
                </div>
                <div>
                    <CheckboxInput
                        label="Display Extended Hours"
                        checked={generalConfig.displayExtendedHours}
                        onToggle={toggleGeneralConfig('displayExtendedHours')}
                    />
                </div>
                <div>
                    <div>Bar size in minutes</div>
                    <TextInput
                        value={barSizeInputText.toString()}
                        onChange={setBarSizeAndUpdateConfig}
                    />
                </div>
                <div className="group order-fills-group">
                    <div className="group-header">
                        <div className="title">Order Fills</div>
                        <ToggleInput
                            checked={isVisible(FillsChartType.OrderFills)}
                            onToggle={toggleChartVisible(
                                FillsChartType.OrderFills
                            )}
                        />
                    </div>

                    <div className="group-header">
                        <div className="title">Comparison Index or Symbol</div>
                    </div>
                    <div>
                        <CheckboxInput
                            className="use-secondary-symbol"
                            label="Default to Newport's secondary symbol"
                            checked={generalConfig.useSecondarySymbol}
                            onToggle={toggleGeneralConfig('useSecondarySymbol')}
                        />
                    </div>
                    <div>
                        <div>
                            {generalConfig.useSecondarySymbol && (
                                <span>Fallback </span>
                            )}
                            Index / Symbol
                        </div>
                        <SymbolInput
                            value={fallbackIndexInputText}
                            onChange={setFallbackIndexAndUpdateConfig}
                        />
                    </div>
                    <div className="group-header">
                        <div className="title">Price</div>
                    </div>
                    <div>
                        <CheckboxInput
                            label="Price Line"
                            checked={orderFillsConfig.displayPrice}
                            onToggle={toggleOrderFillsConfig('displayPrice')}
                        />
                    </div>
                    <div className="price-candle-settings">
                        <CheckboxInput
                            label="Candles (OHLC)"
                            checked={orderFillsConfig.displayPriceCandles}
                            className="price-candles-checkbox"
                            onToggle={toggleOrderFillsConfig(
                                'displayPriceCandles'
                            )}
                        />{' '}
                        {orderFillsConfig.displayPriceCandles && (
                            <CheckboxInput
                                label="Show Candle Legends"
                                checked={
                                    !orderFillsConfig.hidePriceCandleLegends
                                }
                                className="price-candle-legends-checkbox"
                                onToggle={toggleOrderFillsConfig(
                                    'hidePriceCandleLegends'
                                )}
                            />
                        )}
                    </div>
                    <div className="decimals">
                        <NumericValueEditor
                            className="decimals"
                            value={avgExecAndVwapDecimalsInputText}
                            label="Avg Exec/xVWAP decimal places"
                            max={4}
                            min={0}
                            onChange={onAvgExecAndVwapDecimalsChanged}
                        />
                    </div>
                    <div className="group-header">
                        <div className="title">My Order</div>
                    </div>
                    <div>
                        <CheckboxInput
                            label="Order Fill Size"
                            checked={orderFillsConfig.displayOrderFillSize}
                            onToggle={toggleOrderFillsConfig(
                                'displayOrderFillSize'
                            )}
                        />
                    </div>
                    <div>
                        <CheckboxInput
                            label="Arrival and End Labels"
                            checked={!orderFillsConfig.hideArrivalAndAndLabels}
                            onToggle={toggleOrderFillsConfig(
                                'hideArrivalAndAndLabels'
                            )}
                        />
                    </div>
                    <div>
                        <CheckboxInput
                            label="Algorithm Parameters Revisions"
                            checked={
                                orderFillsConfig.displayAlgorithmParametersRevisions
                            }
                            onToggle={toggleOrderFillsConfig(
                                'displayAlgorithmParametersRevisions'
                            )}
                        />
                    </div>
                    <div>
                        <CheckboxInput
                            label="Limit Price"
                            checked={orderFillsConfig.displayLimitPrice}
                            onToggle={toggleOrderFillsConfig(
                                'displayLimitPrice'
                            )}
                        />
                    </div>

                    <div className="group-header">
                        <div className="title">Benchmarks</div>
                    </div>
                    <div>
                        <CheckboxInput
                            label="Arrival"
                            checked={orderFillsConfig.displayArrival}
                            onToggle={toggleOrderFillsConfig('displayArrival')}
                        />
                    </div>
                    <div>
                        <CheckboxInput
                            label="Previous Close"
                            checked={orderFillsConfig.displayClose}
                            onToggle={toggleOrderFillsConfig('displayClose')}
                        />
                    </div>
                    <div>
                        <CheckboxInput
                            label="Open"
                            checked={!orderFillsConfig.hideOpen}
                            onToggle={toggleOrderFillsConfig('hideOpen')}
                        />
                    </div>
                </div>
            </div>
            <FillsGraphExpectedVolumeSettings
                settings={expectedVsActualConfig}
                isVisible={isVisible(FillsChartType.ExpectedVsActual)}
                toggleIsVisible={toggleChartVisible(
                    FillsChartType.ExpectedVsActual
                )}
                updateSettings={(updated: ExpectedVsActualConfig) => {
                    onFillsGraphConfigurationChange({
                        ...fillsGraphConfiguration,
                        [FillsChartType.ExpectedVsActual]: updated,
                    });
                }}
            />
            <div className="group group-percent-of-market-vol">
                <div className="group-header">
                    <div className="title">Participation Rate</div>
                    <ToggleInput
                        checked={isVisible(
                            FillsChartType.PercentageOfMarketVolume
                        )}
                        onToggle={toggleChartVisible(
                            FillsChartType.PercentageOfMarketVolume
                        )}
                    />
                </div>
                <div>
                    <CheckboxInput
                        label="Min Participation"
                        checked={
                            percentageOfMarketVolumeConfig.displayMinParticipationRate
                        }
                        onToggle={togglePercentageOfMarketVolumeConfig(
                            'displayMinParticipationRate'
                        )}
                    />
                </div>
                <div>
                    <CheckboxInput
                        label="Max Participation"
                        checked={
                            percentageOfMarketVolumeConfig.displayMaxParticipationRate
                        }
                        onToggle={togglePercentageOfMarketVolumeConfig(
                            'displayMaxParticipationRate'
                        )}
                    />
                </div>
            </div>
            <div className="group group-pnl">
                <div className="group-header">
                    <div className="title">P&L</div>
                    <ToggleInput
                        checked={isVisible(FillsChartType.PnL)}
                        onToggle={toggleChartVisible(FillsChartType.PnL)}
                    />
                </div>
                {isVisible(FillsChartType.PnL) && (
                    <>
                        <div>
                            <PLBenchmarkDropdown />
                        </div>
                        {isAppModeNewportSingleWidget && (
                            <AppSettingsEditor
                                appSettings={appSettings}
                                onAppSettingsChanged={dispatchUpdateAppSettings}
                                specifyVisibleControls={{}}
                            />
                        )}
                    </>
                )}
            </div>
            <div className="group group-perf-vs-index">
                <div className="group-header">
                    <div className="title">Index-Relative Performance</div>
                    <ToggleInput
                        checked={isVisible(FillsChartType.PerformanceVsIndex)}
                        onToggle={toggleChartVisible(
                            FillsChartType.PerformanceVsIndex
                        )}
                    />
                </div>
                {isVisible(FillsChartType.PerformanceVsIndex) && (
                    <>
                        {isAppModeNewportSingleWidget && (
                            <AppSettingsEditor
                                appSettings={appSettings}
                                onAppSettingsChanged={dispatchUpdateAppSettings}
                                specifyVisibleControls={{
                                    symbology: true,
                                }}
                            />
                        )}
                    </>
                )}
            </div>

            {isAppModeNewportSingleWidget && (
                <AppSettingsEditor
                    appSettings={appSettings}
                    onAppSettingsChanged={dispatchUpdateAppSettings}
                    specifyVisibleControls={{
                        showChartHelp: true,
                        showTooltips: true,
                    }}
                />
            )}
        </div>
    );
}
